/**
 * Calendario contractual de ingresos — periodos de 30 días.
 *
 * Vigencia: 03/05/2026 → 28/04/2027 (360 días, 12 cortes).
 * Cada corte cierra a las 23:59:59 hora Argentina (UTC-3).
 * Renovación NO automática.
 *
 * Las fechas se almacenan como `YYYY-MM-DD` interpretadas en hora ARG; el
 * backend (api/admin/revenue.ts) convierte a UTC al recibir la query.
 */

export interface ContractPeriod {
  /** 1-indexed period number. */
  index: number;
  /** Start date inclusive (ARG-local YYYY-MM-DD). */
  start: string;
  /** End date inclusive (ARG-local YYYY-MM-DD), the corte. */
  end: string;
  /** Friendly label "Período N". */
  label: string;
}

export const CONTRACT_START = "2026-05-03";
export const CONTRACT_END = "2027-04-28";
export const CONTRACT_TOTAL_DAYS = 360;

/**
 * Cortes del contrato (fechas literales del documento).
 * Cada item es la fecha del corte (último día inclusive del período).
 */
const CUT_DATES = [
  "2026-06-02",
  "2026-07-02",
  "2026-08-01",
  "2026-08-31",
  "2026-09-30",
  "2026-10-30",
  "2026-11-29",
  "2026-12-29",
  "2027-01-28",
  "2027-02-27",
  "2027-03-29",
  "2027-04-28",
] as const;

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/**
 * Lista de los 12 períodos del contrato.
 * Período 1 arranca en CONTRACT_START; cada período siguiente empieza al
 * día siguiente del corte previo.
 */
export const CONTRACT_PERIODS: readonly ContractPeriod[] = (() => {
  const periods: ContractPeriod[] = [];
  let cursor = CONTRACT_START;
  CUT_DATES.forEach((cut, i) => {
    periods.push({
      index: i + 1,
      start: cursor,
      end: cut,
      label: `Período ${i + 1}`,
    });
    cursor = addDaysIso(cut, 1);
  });
  return periods;
})();

/**
 * Devuelve el período del contrato que contiene la fecha pasada (ARG-local
 * YYYY-MM-DD). `null` si la fecha está fuera del contrato.
 */
export function findCurrentPeriod(todayIso: string): ContractPeriod | null {
  return (
    CONTRACT_PERIODS.find((p) => p.start <= todayIso && todayIso <= p.end) ?? null
  );
}

/**
 * Conversión por defecto, igualada al checkout (USD → ARS = 1.420).
 * El cociente EUR/USD = 0.85 (mismo default que checkout Europa) implica
 * 1 EUR ≈ 1 / 0.85 USD → ARS = 1.420 / 0.85 ≈ 1670.59.
 */
export const DEFAULT_USD_TO_ARS = 1420;
export const DEFAULT_EUR_TO_USD = 0.85; // 1 USD = 0.85 EUR
export const DEFAULT_EUR_TO_ARS = DEFAULT_USD_TO_ARS / DEFAULT_EUR_TO_USD;
